import { CommonActions, useNavigation } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlatList,
  Modal,
  Platform,
  TouchableOpacity,
  TouchableWithoutFeedback,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';

import { ModalHeader } from '@components/Headers/ModalHeader';
import { Box, ShadowBox, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Overlay from '@components/Tutorialization/Overlay';
import TooltipPopup from '@components/Tutorialization/TooltipPopup';
import { MemberRole, MemberType } from '@components/User/MemberRole';
import { User, TaskMemberRole, ProjectMemberRole } from '@graphql/generated';
import { useProjectTutorial } from '@hooks/useProjectTutorial';
import theme from '@themes/theme';
import { pixelSizeHorizontal } from '@utils/normalise';
import { AsyncStorage } from '@utils/storage';

const MemberListItemSeparator = () => <Box marginTop='m' />;
const MemberListFooter = () => <Box marginBottom='listFooter' />;
const MemberListHeader = () => <Box marginTop='m' />;

interface EditNewMemberRolesProps<Roles> {
  updateMemberRole: (id: User['id'], role: Roles) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  isTaskAssignee: boolean;
  usersAndRoles?: MemberType<Roles>[];
  roles: string[];
  currentRole?: string | null;
  pricingInfo?: string | null;
  goBack?: boolean;
  teamUUId?: string;
  roleDescriptions?: Partial<Record<string, string>>;
}

export const EditNewMemberRoles = <Roles,>({
  updateMemberRole,
  onSubmit,
  isSubmitting,
  isTaskAssignee,
  usersAndRoles,
  roles,
  currentRole,
  pricingInfo,
  goBack,
  teamUUId,
  roleDescriptions,
}: EditNewMemberRolesProps<Roles>) => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const {
    isVisible,

    addMembersTutorialState,
    setHeaderOverlayActive,
    setAddMemeberTutorialStateAndInStorage,
    setProjectTutorialStateAndInStorage,
    headerOverlayActive,
    projectTutorialState,
  } = useProjectTutorial();

  useEffect(() => {
    if (isVisible && addMembersTutorialState === '3') {
      setHeaderOverlayActive(true);
      return;
    }
    setHeaderOverlayActive(false);
    return () => setHeaderOverlayActive(false);
  }, [isVisible, addMembersTutorialState]);

  let memberRole;
  if (isTaskAssignee) {
    memberRole = [TaskMemberRole.Viewer];
  } else if (currentRole == ProjectMemberRole.Editor) {
    memberRole = [ProjectMemberRole.Editor, ProjectMemberRole.Viewer];
  } else {
    memberRole = roles;
  }

  return (
    <Box flex={1} backgroundColor='white'>
      {headerOverlayActive && (
        <Overlay
          backgroundColor='rgba(0, 0, 0, 0.5)'
          visible={isVisible && addMembersTutorialState === '3'}
          children={
            <Box
              position='absolute'
              top='10%'
              left='15%'
              pointerEvents='box-none'>
              <TooltipPopup
                topRightPos={pixelSizeHorizontal(25)}
                animate
                visible={isVisible && addMembersTutorialState === '3'}
                title='Change Role'
                description='You can change the roles of your members by clicking on drop-down.'
                pointerPosition='top-right'
                primaryBtn={async () => {
                  await AsyncStorage.setItem(
                    'tutorial_add_member',
                    'completed'
                  );
                  setAddMemeberTutorialStateAndInStorage('4');
                  setHeaderOverlayActive(false);

                  if (projectTutorialState === '10') {
                    return;
                  }

                  setProjectTutorialStateAndInStorage(
                    (Number(projectTutorialState) + 1).toString()
                  );
                }}
                primaryBtnTitle='Got it'
              />
            </Box>
          }
        />
      )}
      <Modal animationType='fade' transparent={true} visible={modalOpen}>
        <Box style={teamStyles.modalContainer}>
          <TouchableWithoutFeedback
            onPress={() => setModalOpen(!modalOpen)}
            style={teamStyles.modal}
            accessibilityLabel='CloseModalButton'>
            <Box style={teamStyles.modal} />
          </TouchableWithoutFeedback>

          <ShadowBox
            variant='thin'
            style={
              Platform.OS === 'web' ? teamStyles.popUpWeb : teamStyles.popUp
            }>
            <Box
              marginVertical='m'
              marginHorizontal='m'
              flex={1}
              justifyContent='space-between'>
              <Box
                style={teamStyles.modalHeader}
                accessibilityLabel='RoleSelectModal'>
                <ModalHeader
                  title={t('shared:pricing')}
                  submitLabel={t('shared:cancel')}
                  onCancel={() => setModalOpen(false)}
                  showCancel={false}
                  onSubmit={() => setModalOpen(false)}
                />
              </Box>
              <Text>{pricingInfo}</Text>
              <FlatList
                data={usersAndRoles}
                renderItem={({ item }) => {
                  return (
                    <Box>
                      <MemberRole<Roles>
                        roles={memberRole}
                        member={item}
                        roleDescriptions={roleDescriptions}
                      />
                    </Box>
                  );
                }}
                showsHorizontalScrollIndicator={Platform.OS !== 'web'}
                showsVerticalScrollIndicator={false}
                keyExtractor={(item) => item.user.id}
                ItemSeparatorComponent={MemberListItemSeparator}
                ListFooterComponent={MemberListFooter}
                ListHeaderComponent={MemberListHeader}
              />

              <Box flexDirection='row' mt='m' flex={1}>
                <Button
                  variant='primary'
                  disabled={isSubmitting}
                  onPress={() => {
                    setLoading(true);
                    onSubmit();
                    setModalOpen(false);
                    if (goBack) {
                      navigation.dispatch(
                        CommonActions.navigate({
                          name: 'team-members',
                          params: { teamUUId },
                        })
                      );
                      setLoading(false);
                      return;
                    }
                    navigation.dispatch(
                      CommonActions.reset({
                        index: 0,
                        routes: [{ name: 'home' }],
                      })
                    );
                  }}
                  fullWidth={true}>
                  {isSubmitting || loading ? (
                    <ActivityIndicator color='black' size='small' />
                  ) : (
                    t('shared:confirm')
                  )}
                </Button>
              </Box>
            </Box>
          </ShadowBox>
        </Box>
      </Modal>
      <FlatList
        accessibilityLabel='user-role-list'
        data={usersAndRoles?.filter((item) => item.role !== 'OWNER')}
        renderItem={({ item }) => {
          return (
            <Box marginLeft='m' marginRight='l'>
              <MemberRole<Roles>
                roles={memberRole}
                member={item}
                updateRole={(role: Roles) => {
                  updateMemberRole(item.user.id, role);
                }}
                roleDescriptions={roleDescriptions}
              />
            </Box>
          );
        }}
        showsHorizontalScrollIndicator={Platform.OS !== 'web'}
        showsVerticalScrollIndicator={false}
        keyExtractor={(item) => item.user.id}
        ItemSeparatorComponent={MemberListItemSeparator}
        ListFooterComponent={MemberListFooter}
        ListHeaderComponent={MemberListHeader}
      />

      {Platform.OS === 'web' ? (
        <Box
          flexDirection='row'
          alignItems='center'
          justifyContent='flex-end'
          mr='l'
          marginVertical='m'>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            disabled={isSubmitting}>
            <Text>{t('shared:back')}</Text>
          </TouchableOpacity>
          <Button
            variant='primary'
            disabled={isSubmitting}
            onPress={() => {
              if (pricingInfo) setModalOpen(true);
              else onSubmit();
            }}
            marginLeft='l'
            accessibilityLabel='add members button'>
            {t('shared:addMembers')}
          </Button>
        </Box>
      ) : (
        <Button
          variant='primary'
          disabled={isSubmitting}
          onPress={() => {
            if (pricingInfo) setModalOpen(true);
            else onSubmit();
          }}
          float='bottom-inset'>
          {isSubmitting ? <ActivityIndicatorLoading /> : t('shared:confirm')}
        </Button>
      )}
    </Box>
  );
};

export const teamStyles = StyleSheet.create({
  modal: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'black',
    opacity: 0.5,
  },
  modalContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalHeader: {
    paddingBottom: theme.spacing.m,
    paddingTop: theme.spacing.l,
  },
  popUp: {
    position: 'absolute',
    backgroundColor: 'white',
    width: '100%',
    bottom: 0,
    paddingBottom: theme.spacing.xxl,
    borderTopLeftRadius: theme.borderRadii.l,
    borderTopRightRadius: theme.borderRadii.l,
  },
  popUpWeb: {
    position: 'absolute',
    backgroundColor: 'white',
    width: 500,
    maxHeight: 600,
    paddingBottom: theme.spacing.l,
    borderRadius: theme.borderRadii.s,
  },
});
